/**
 * Firebase app initialization for the RealPranks redesign.
 *
 * Uses the modular v9+ SDK. All exports below are lazy singletons, so importing
 * this module does not connect to Firebase until one of them is used.
 *
 * Environment variables are read from Vite at build time:
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_STORAGE_BUCKET
 *   VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_APP_ID
 *   VITE_FIREBASE_MEASUREMENT_ID (optional)
 *
 * For local development against the Firebase Emulator Suite, set
 *   VITE_FIREBASE_USE_EMULATORS=true
 * and (optionally) the host/port overrides documented below.
 */

import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";

import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from "firebase/functions";

type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

const readEnv = (key: string, fallback = ""): string => {
  // Vite exposes `import.meta.env` at build time.
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  return value ?? fallback;
};

const isLikelyLovablePreview = (): boolean => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  // Covers current Lovable preview hosts and future subdomains that include
  // the "lovable" token.
  return host.includes("lovable");
};

/**
 * Opt-in profile switch:
 * - strict  (default): requires VITE_FIREBASE_* and throws if missing
 * - lovable: falls back to a non-secret web config so cloud previews boot
 */
const firebaseProfile = readEnv("VITE_FIREBASE_PROFILE", "strict");
const allowLovableFallback =
  firebaseProfile === "lovable" || isLikelyLovablePreview();

const firebaseConfig: Partial<FirebaseWebConfig> = {
  apiKey: readEnv("VITE_FIREBASE_API_KEY"),
  authDomain: readEnv("VITE_FIREBASE_AUTH_DOMAIN"),
  projectId: readEnv("VITE_FIREBASE_PROJECT_ID"),
  storageBucket: readEnv("VITE_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: readEnv("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  appId: readEnv("VITE_FIREBASE_APP_ID"),
  measurementId: readEnv("VITE_FIREBASE_MEASUREMENT_ID") || undefined,
};

/**
 * Safe to ship to the browser (Firebase web config is public by design).
 * Use this only for non-production preview environments (Lovable profile).
 */
const LOVABLE_FALLBACK_CONFIG: FirebaseWebConfig = {
  apiKey: "AIzaSyAW8rWTjVCSLPsArlEEhm4hDeUJbaueh30",
  authDomain: "rosscreations-01.firebaseapp.com",
  projectId: "rosscreations-01",
  storageBucket: "rosscreations-01.appspot.com",
  messagingSenderId: "757765874703",
  appId: "1:757765874703:web:d48cd3aaf88b3b23ed6f7a",
  measurementId: "G-HMKQEVFP6V",
};

let _warnedLovableFallback = false;

/** The Cloud Functions region we deploy to. */
export const FIREBASE_FUNCTIONS_REGION = "us-central1";

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _functions: Functions | null = null;
let _emulatorsWired = false;

/**
 * Returns the singleton Firebase app, initializing it on first call.
 *
 * Safe to call on both the client and the server; during SSR renders that
 * never touch Firebase, this function is never invoked.
 */
export function getFirebaseApp(): FirebaseApp {
  if (_app) return _app;
  if (getApps().length > 0) {
    _app = getApps()[0]!;
    return _app;
  }

  const hasRequiredRuntimeConfig =
    !!firebaseConfig.apiKey &&
    !!firebaseConfig.authDomain &&
    !!firebaseConfig.projectId &&
    !!firebaseConfig.storageBucket &&
    !!firebaseConfig.messagingSenderId &&
    !!firebaseConfig.appId;

  if (!hasRequiredRuntimeConfig && !allowLovableFallback) {
    throw new Error(
      "[firebase] Missing VITE_FIREBASE_* env vars. Copy .env.example to .env and fill in the web app config from the Firebase console. For Lovable preview-only fallback, set VITE_FIREBASE_PROFILE=lovable.",
    );
  }

  if (hasRequiredRuntimeConfig) {
    _app = initializeApp(firebaseConfig as FirebaseWebConfig);
    return _app;
  }

  if (!_warnedLovableFallback) {
    _warnedLovableFallback = true;
    console.warn(
      "[firebase] Lovable fallback is enabled and VITE_FIREBASE_* vars are missing. Falling back to embedded preview config. Use a dedicated staging Firebase project in Lovable for safest testing.",
    );
  }
  _app = initializeApp(LOVABLE_FALLBACK_CONFIG);
  return _app;
}

export function getFirebaseAuth(): Auth {
  if (_auth) return _auth;
  _auth = getAuth(getFirebaseApp());
  wireEmulatorsIfNeeded();
  return _auth;
}

export function getDb(): Firestore {
  if (_db) return _db;
  const app = getFirebaseApp();
  try {
    const previewTransportSettings = isLikelyLovablePreview()
      ? {
          experimentalForceLongPolling: true,
          useFetchStreams: false,
        }
      : {
          experimentalAutoDetectLongPolling: true,
        };

    // Lovable preview proxies can break Firestore's WebChannel/fetch-stream
    // transport during route changes. In preview we force classic long polling;
    // elsewhere we keep auto-detection so production can use the fastest path.
    _db = initializeFirestore(app, previewTransportSettings);
  } catch {
    // initializeFirestore throws if it was already called; fall back to the
    // existing instance.
    _db = getFirestore(app);
  }
  wireEmulatorsIfNeeded();
  return _db;
}


export function getFirebaseStorage(): FirebaseStorage {
  if (_storage) return _storage;
  _storage = getStorage(getFirebaseApp());
  wireEmulatorsIfNeeded();
  return _storage;
}

export function getFirebaseFunctions(): Functions {
  if (_functions) return _functions;
  _functions = getFunctions(getFirebaseApp(), FIREBASE_FUNCTIONS_REGION);
  wireEmulatorsIfNeeded();
  return _functions;
}

/**
 * Connects the SDK to the Firebase Emulator Suite when
 * `VITE_FIREBASE_USE_EMULATORS=true`. Wires are idempotent and attached once
 * across the module lifetime.
 *
 * Defaults (matches the `firebase.json` emulator block at the repo root):
 *   auth:       9099
 *   firestore:  8080
 *   storage:    9199
 *   functions:  5001
 */
function wireEmulatorsIfNeeded(): void {
  if (_emulatorsWired) return;
  if (readEnv("VITE_FIREBASE_USE_EMULATORS") !== "true") return;

  const host = readEnv("VITE_FIREBASE_EMULATOR_HOST", "127.0.0.1");

  if (_auth) {
    const port = Number(readEnv("VITE_FIREBASE_AUTH_EMULATOR_PORT", "9099"));
    connectAuthEmulator(_auth, `http://${host}:${port}`, { disableWarnings: true });
  }
  if (_db) {
    const port = Number(readEnv("VITE_FIREBASE_FIRESTORE_EMULATOR_PORT", "8080"));
    connectFirestoreEmulator(_db, host, port);
  }
  if (_storage) {
    const port = Number(readEnv("VITE_FIREBASE_STORAGE_EMULATOR_PORT", "9199"));
    connectStorageEmulator(_storage, host, port);
  }
  if (_functions) {
    const port = Number(readEnv("VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT", "5001"));
    connectFunctionsEmulator(_functions, host, port);
  }
  _emulatorsWired = true;
}
